import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import {
  CreditCard, FileText, Building, Receipt, Calendar,
  Download, ArrowRight, Loader2, CheckCircle2, Clock, AlertCircle,
} from 'lucide-react';

const TABS = [
  { key: 'subscription', label: 'Abbonamento', icon: CreditCard },
  { key: 'receipts', label: 'Ricevute', icon: Receipt },
  { key: 'invoices', label: 'Fatture', icon: FileText },
  { key: 'details', label: 'Dati fatturazione', icon: Building },
  { key: 'payments', label: 'Storico pagamenti', icon: Calendar },
];

const STATUS_STYLE = {
  pending: { bg: '#FEF3C7', fg: '#92400E', label: 'In attesa' },
  issued: { bg: '#D1FAE5', fg: '#065F46', label: 'Emessa' },
  cancelled: { bg: '#FEE2E2', fg: '#991B1B', label: 'Annullata' },
};


function SectionCard({ title, children, action }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-[#1A1A1A]">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ icon: Icon, message }) {
  return (
    <div className="text-center py-12 text-slate-500" data-testid="empty-state">
      <Icon size={32} className="mx-auto mb-2 opacity-40" />
      <p className="text-sm">{message}</p>
    </div>
  );
}


/* ── TAB: SUBSCRIPTION ──────────────────────────────────────────────────── */
function SubscriptionTab() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get('/admin/plans').then(r => setPlan(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader className="h-32" />;

  const expires = plan?.subscription_expires;
  const expiryDate = expires ? new Date(expires).toLocaleDateString('it-IT') : null;
  const isActive = plan?.subscription_status === 'active';

  return (
    <SectionCard title="Il tuo abbonamento Lazo" action={
      <button
        onClick={() => navigate('/admin/plans')}
        data-testid="change-plan-btn"
        className="flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:shadow-md transition-all">
        Cambia piano <ArrowRight size={14} />
      </button>
    }>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-[#FFF5F0] to-white border border-[#D44E20]/20 rounded-xl p-5" data-testid="current-plan-card">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Piano attuale</p>
          <p className="text-2xl font-bold text-[#D44E20]">{plan?.current_package_name || 'Nessuno'}</p>
          {plan?.current_package_slug && (
            <p className="text-xs text-slate-500 mt-1">Slug: {plan.current_package_slug}</p>
          )}
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Stato</p>
          <div className="flex items-center gap-2 mt-1">
            {isActive ? (
              <>
                <CheckCircle2 size={20} className="text-emerald-600" />
                <span className="text-lg font-bold text-emerald-700">Attivo</span>
              </>
            ) : plan?.subscription_status === 'expired' ? (
              <>
                <AlertCircle size={20} className="text-red-600" />
                <span className="text-lg font-bold text-red-700">Scaduto</span>
              </>
            ) : (
              <span className="text-lg font-bold text-slate-500">—</span>
            )}
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Prossimo rinnovo</p>
          <p className="text-lg font-bold text-[#1A1A1A]" data-testid="expiry-date">{expiryDate || '—'}</p>
        </div>
      </div>
    </SectionCard>
  );
}


/* ── TAB: RECEIPTS ──────────────────────────────────────────────────────── */
function ReceiptsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(null);

  const load = () => {
    setLoading(true);
    adminApi.get('/admin/billing/receipts').then(r => setList(r.data.receipts || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async (r) => {
    try {
      const resp = await adminApi.get(`/admin/billing/receipts/${r.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${r.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore download');
    }
  };

  const requestInvoice = async (r) => {
    setRequesting(r.id);
    try {
      await adminApi.post(`/admin/billing/receipts/${r.id}/request-invoice`);
      toast.success('Richiesta inviata — riceverai la fattura appena pronta');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore richiesta');
    }
    setRequesting(null);
  };

  if (loading) return <Loader className="h-32" />;

  return (
    <SectionCard title={`Ricevute (${list.length})`}>
      {list.length === 0 ? (
        <EmptyState icon={Receipt} message="Nessuna ricevuta ancora. Verrà generata in automatico dopo ogni pagamento." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="receipts-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Numero</th>
                <th className="text-left px-4 py-3 font-semibold">Data</th>
                <th className="text-left px-4 py-3 font-semibold">Descrizione</th>
                <th className="text-right px-4 py-3 font-semibold">Totale</th>
                <th className="text-right px-4 py-3 font-semibold">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {list.map(r => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`receipt-row-${r.number}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold">{r.number}</td>
                  <td className="px-4 py-3">{new Date(r.issue_date).toLocaleDateString('it-IT')}</td>
                  <td className="px-4 py-3 text-slate-600">{r.description}</td>
                  <td className="px-4 py-3 text-right font-semibold">{Number(r.amount_total || 0).toFixed(2)} €</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => downloadPdf(r)} data-testid={`download-receipt-${r.number}`}
                        className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#1A1A1A] font-semibold flex items-center gap-1.5">
                        <Download size={12} /> PDF
                      </button>
                      {!r.invoice_requested && !r.invoice_issued ? (
                        <button onClick={() => requestInvoice(r)} disabled={requesting === r.id}
                          data-testid={`request-invoice-${r.number}`}
                          className="text-xs px-3 py-1.5 rounded-lg bg-[#D44E20] text-white hover:shadow-md font-semibold disabled:opacity-50">
                          {requesting === r.id ? <Loader2 size={12} className="animate-spin" /> : 'Richiedi fattura'}
                        </button>
                      ) : r.invoice_issued ? (
                        <span className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 font-semibold flex items-center gap-1">
                          <CheckCircle2 size={12} /> Fattura emessa
                        </span>
                      ) : (
                        <span className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 font-semibold flex items-center gap-1">
                          <Clock size={12} /> In attesa
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}


/* ── TAB: INVOICES ──────────────────────────────────────────────────────── */
function InvoicesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get('/admin/billing/invoices').then(r => setList(r.data.invoices || [])).finally(() => setLoading(false));
  }, []);

  const downloadPdf = async (r) => {
    try {
      const resp = await adminApi.get(`/admin/billing/invoices/${r.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fattura_${r.fiscal_invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore download');
    }
  };

  if (loading) return <Loader className="h-32" />;

  return (
    <SectionCard title={`Fatture fiscali (${list.length})`}>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-sm text-blue-900">
        <p className="font-semibold mb-1">Come funziona?</p>
        <p className="text-xs leading-relaxed">
          Le fatture fiscali sono emesse dal nostro sistema di fatturazione elettronica (SDI) dopo la tua richiesta. 
          Vai alla sezione "Ricevute" e clicca "Richiedi fattura" sulla ricevuta che ti serve. 
          Riceverai qui il PDF ufficiale appena pronto (di solito entro 2-3 giorni lavorativi).
        </p>
      </div>
      {list.length === 0 ? (
        <EmptyState icon={FileText} message="Nessuna fattura ancora richiesta." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Ricevuta</th>
                <th className="text-left px-4 py-3 font-semibold">Data richiesta</th>
                <th className="text-left px-4 py-3 font-semibold">Nr. fattura</th>
                <th className="text-left px-4 py-3 font-semibold">Stato</th>
                <th className="text-right px-4 py-3 font-semibold">Totale</th>
                <th className="text-right px-4 py-3 font-semibold">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {list.map(r => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold">{r.receipt_number}</td>
                    <td className="px-4 py-3">{new Date(r.requested_at).toLocaleDateString('it-IT')}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.fiscal_invoice_number || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: st.bg, color: st.fg }}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{Number(r.amount_total || 0).toFixed(2)} €</td>
                    <td className="px-4 py-3 text-right">
                      {r.status === 'issued' ? (
                        <button onClick={() => downloadPdf(r)} data-testid={`download-invoice-${r.id}`}
                          className="text-xs px-3 py-1.5 rounded-lg bg-[#D44E20] text-white hover:shadow-md font-semibold flex items-center gap-1.5 ml-auto">
                          <Download size={12} /> Scarica PDF
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}


/* ── TAB: BILLING DETAILS ───────────────────────────────────────────────── */
function BillingDetailsTab() {
  const [form, setForm] = useState({
    company_name: '', vat_number: '', tax_code: '',
    address: '', city: '', zip_code: '', country: 'Italia',
    sdi_code: '', pec_email: '', email: '', phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/admin/billing/details').then(r => setForm(f => ({ ...f, ...r.data }))).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put('/admin/billing/details', form);
      toast.success('Dati di fatturazione salvati');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
    setSaving(false);
  };

  if (loading) return <Loader className="h-32" />;

  const field = (key, label, placeholder = '', type = 'text', required = false) => (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={form[key] || ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        data-testid={`billing-${key}`}
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20] transition-colors"
      />
    </div>
  );

  return (
    <SectionCard title="Dati di fatturazione" action={
      <button onClick={save} disabled={saving} data-testid="save-billing-details"
        className="flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:shadow-md disabled:opacity-50">
        {saving ? <Loader2 size={14} className="animate-spin" /> : 'Salva'}
      </button>
    }>
      <p className="text-xs text-slate-500 mb-4">
        Questi dati verranno usati per emettere le fatture fiscali a tuo nome. La P.IVA è obbligatoria per richiedere una fattura.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {field('company_name', 'Ragione sociale', 'Es. Taco Loco Srl', 'text', true)}
        {field('vat_number', 'Partita IVA', 'IT12345678901', 'text', true)}
        {field('tax_code', 'Codice fiscale', '', 'text')}
        {field('sdi_code', 'Codice SDI', '7 caratteri alfanumerici')}
        {field('pec_email', 'PEC', 'nome@pec.it', 'email')}
        {field('email', 'Email amministrativa', 'admin@miolocale.it', 'email')}
        {field('phone', 'Telefono', '')}
        <div />
        <div className="md:col-span-2">
          {field('address', 'Indirizzo', 'Via Roma, 12')}
        </div>
        {field('zip_code', 'CAP', '00100')}
        {field('city', 'Città', 'Roma')}
        {field('country', 'Paese', 'Italia')}
      </div>
    </SectionCard>
  );
}


/* ── TAB: PAYMENTS HISTORY ──────────────────────────────────────────────── */
function PaymentsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get('/admin/billing/payments').then(r => setList(r.data.payments || [])).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader className="h-32" />;

  const statusChip = (s) => {
    const map = {
      paid: { bg: '#D1FAE5', fg: '#065F46', label: 'Pagato' },
      initiated: { bg: '#FEF3C7', fg: '#92400E', label: 'In corso' },
      failed: { bg: '#FEE2E2', fg: '#991B1B', label: 'Fallito' },
    };
    const v = map[s] || { bg: '#E2E8F0', fg: '#475569', label: s || '—' };
    return <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: v.bg, color: v.fg }}>{v.label}</span>;
  };

  return (
    <SectionCard title={`Storico pagamenti (${list.length})`}>
      {list.length === 0 ? (
        <EmptyState icon={Calendar} message="Nessun pagamento registrato." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Data</th>
                <th className="text-left px-4 py-3 font-semibold">Tipo</th>
                <th className="text-left px-4 py-3 font-semibold">Descrizione</th>
                <th className="text-left px-4 py-3 font-semibold">Metodo</th>
                <th className="text-left px-4 py-3 font-semibold">Stato</th>
                <th className="text-right px-4 py-3 font-semibold">Importo</th>
              </tr>
            </thead>
            <tbody>
              {list.map(p => {
                const isPaypal = (p.type || '').endsWith('_paypal');
                const typeLabel = p.type?.startsWith('subscription') ? 'Abbonamento' : p.type?.startsWith('addon') ? 'Add-on' : p.type || '—';
                const desc = p.metadata?.package_name || p.metadata?.addon_name || '—';
                return (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">{new Date(p.created_at).toLocaleString('it-IT')}</td>
                    <td className="px-4 py-3 text-slate-600">{typeLabel}</td>
                    <td className="px-4 py-3 font-medium">{desc}</td>
                    <td className="px-4 py-3 text-slate-600">{isPaypal ? 'PayPal' : 'Carta (Stripe)'}</td>
                    <td className="px-4 py-3">{statusChip(p.payment_status)}</td>
                    <td className="px-4 py-3 text-right font-semibold">{Number(p.amount || 0).toFixed(2)} €</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}


function Loader({ className = '' }) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <Loader2 size={24} className="animate-spin text-[#D44E20]" />
    </div>
  );
}


export default function AdminBilling() {
  const [tab, setTab] = useState('subscription');

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-billing-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1A1A1A]">Fatturazione</h1>
        <p className="text-sm text-slate-500 mt-1">Gestisci abbonamento, ricevute, fatture e dati fiscali.</p>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`billing-tab-${key}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${
              tab === key ? 'border-[#D44E20] text-[#D44E20]' : 'border-transparent text-slate-500 hover:text-[#1A1A1A]'
            }`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'subscription' && <SubscriptionTab />}
      {tab === 'receipts' && <ReceiptsTab />}
      {tab === 'invoices' && <InvoicesTab />}
      {tab === 'details' && <BillingDetailsTab />}
      {tab === 'payments' && <PaymentsTab />}
    </div>
  );
}
